import { useEffect, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { SearchForMedicalPaOrdersFromPostgresParams } from "../../../../api/postgreSQL/postCalls/searchForMedicalPaOrdersFromPostgres";
import { useTablesContext } from "../../../../context/tablesContextProvider";
import { FilterValues } from "../../../../data-model/filterValues";
import { TableNames } from "../../../../enums/tableNames";
import { RootState } from "../../../../redux/store/store";
import { getOrgIdForFetchExternalWorklist } from "../../../../utils/organizationHelper";
import { generateFilterPayloadForReviewPendingOrders } from "../utils/generateFilterPayloadForReviewPendingOrders";

/**
 * Builds and maintains the Postgres search payload for Review Pending Orders based on
 * active filters, search term, org ID, user ID, auth status segment, and the persisted
 * sort state from `tablesContext` (`sortByField` / `sortAscending`). Defers payload
 * generation until after the initial data fetch completes to avoid duplicate requests.
 * Uses a single effect with a ref guard so the payload is built immediately once the
 * initial fetch finishes, and recalculated on any dependency change.
 *
 * Re-running on sort changes is what makes column-header clicks trigger a server-side
 * refetch via the downstream filter/search effect, since `CustomTable` writes
 * `sortByField`/`sortAscending` into context on each sort click.
 *
 * @param validFilters - Currently applied filter values
 * @param searchTerm - Free-text search string
 * @param initialDataFetchingExecuted - Whether the initial table data load has finished
 * @param authStatus - Optional auth status segment to scope the payload
 * @returns The current filterPayload and its setter
 */
export const useFilterPayload = (
  validFilters: FilterValues[],
  searchTerm: string,
  initialDataFetchingExecuted: boolean,
  authStatus?: string,
) => {
  const { user } = useSelector(
    (state: RootState) => state.firebaseAuthentication,
  );
  const orgId = getOrgIdForFetchExternalWorklist();
  const { getTableDataFromContext } = useTablesContext();
  const tableName = authStatus
    ? `${TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA}__${authStatus}`
    : TableNames.REVIEW_PENDING_ORDERS_FROM_ALGOLIA;
  const tableAttributes = getTableDataFromContext(tableName);
  const sortByField = tableAttributes?.sortByField;
  const sortAscending = tableAttributes?.sortAscending;

  const [filterPayload, setFilterPayload] =
    useState<SearchForMedicalPaOrdersFromPostgresParams | null>(null);

  const hasInitialFetchCompleted = useRef(false);

  useEffect(() => {
    if (initialDataFetchingExecuted && !hasInitialFetchCompleted.current) {
      hasInitialFetchCompleted.current = true;
    }

    if (!hasInitialFetchCompleted.current) return;

    const payload = generateFilterPayloadForReviewPendingOrders(
      validFilters,
      searchTerm,
      orgId,
      user?.id ?? "",
      authStatus,
      sortByField,
      sortAscending,
    );
    setFilterPayload(payload);
  }, [
    initialDataFetchingExecuted,
    validFilters,
    searchTerm,
    orgId,
    user?.id,
    authStatus,
    sortByField,
    sortAscending,
  ]);

  return { filterPayload, setFilterPayload };
};
